import { getAuthentication } from './authentication';

export const DOMAINS = {
    'elektra-app': import.meta.env.VITE_DOMAIN_ELEKTRA_APP!,
    'portaal': import.meta.env.VITE_DOMAIN_PORTAAL!,
} as const;

export type Domain = keyof typeof DOMAINS;
export type DomainValue = typeof DOMAINS[Domain];

/**
 * Array of domain keys for use in validation schemas (e.g., z.enum)
 */
export const DOMAIN_KEYS = Object.keys(DOMAINS) as readonly Domain[];

// Default allowed hosts per environment (fallback when env var is not set)
const DEFAULT_ALLOWED_HOSTS: Record<string, readonly string[]> = {
    development: [
        'localhost',
        'app.elektra.cdab.nl',
        'portaal.cdab.nl',
    ],
    production: [
        'test.elektra.connected.nl',
        'test.portaal.connected.nl',
        'demo.elektra.connected.nl',
        'demo.portaal.connected.nl',
        'elektra.connected.nl',
        'portaal.connected.nl',
    ],
} as const;

/**
 * Get the list of allowed redirect hosts for the current environment
 * Reads from VITE_ALLOWED_REDIRECT_HOSTS env var (comma-separated), falls back to defaults
 *
 * @returns Array of allowed hostnames
 *
 * @example
 * // With env var: VITE_ALLOWED_REDIRECT_HOSTS=elektra.connected.nl,portaal.connected.nl
 * getAllowedHosts() // ['elektra.connected.nl', 'portaal.connected.nl']
 */
export function getAllowedHosts(): readonly string[] {
    const envHosts = import.meta.env.VITE_ALLOWED_REDIRECT_HOSTS;

    if (envHosts && typeof envHosts === 'string') {
        return envHosts.split(',').map(host => host.trim()).filter(Boolean);
    }

    // Fall back to defaults based on MODE
    // In development, allow all hosts for easier local testing
    const mode = import.meta.env.MODE || 'development';

    return DEFAULT_ALLOWED_HOSTS[mode] ?? DEFAULT_ALLOWED_HOSTS.production;
}

/**
 * Securely validate if a host is in the allowed hosts list
 * Uses exact hostname matching to prevent subdomain attacks
 *
 * @param host - The host to validate (e.g., 'localhost:5173', 'elektra.connected.nl')
 * @param allowedHosts - Optional list of allowed hosts (defaults to getAllowedHosts())
 * @returns true if the host is allowed, false otherwise
 *
 * @security This function extracts the hostname (without port) and performs exact matching
 * to prevent attacks like 'elektra.connected.nl.malicious.com' bypassing validation
 *
 * @example
 * isAllowedHost('localhost:5173') // true (in development)
 * isAllowedHost('elektra.connected.nl') // true (in production)
 * isAllowedHost('elektra.connected.nl.malicious.com') // false - subdomain attack blocked
 */
export function isAllowedHost(host: string, allowedHosts?: readonly string[]): boolean {
    const hosts = allowedHosts ?? getAllowedHosts();

    // Extract hostname without port for comparison
    let hostname: string;
    try {
        // Handle hosts that might include protocol or just be hostname:port
        const normalizedHost = host.includes('://') ? host : `https://${host}`;
        const url = new URL(normalizedHost);
        hostname = url.hostname;
    }
    catch {
        // If URL parsing fails, use the host as-is (strip port manually)
        hostname = host.split(':')[0];
    }

    // Exact match against allowed hosts (also strip ports from allowed hosts)
    return hosts.some((allowedHost) => {
        const allowedHostname = allowedHost.split(':')[0];
        return hostname === allowedHostname;
    });
}

/**
 * Build a full URL from a domain name
 * @param domain - The domain without protocol (e.g., 'portaal.cdab.nl')
 * @param path - Optional path to append to the URL
 * @returns The full URL with https protocol
 *
 * @example
 * buildUrl('portaal.cdab.nl', '/login') // 'https://portaal.cdab.nl/login'
 */
export function buildUrl(domain: string, path: string = ''): string {
    const protocol = domain.includes('localhost') ? 'http:' : 'https:';
    const url = new URL(path, `${protocol}//${domain}`);
    return url.toString();
}

/**
 * Get the portal domain from DOMAINS configuration
 * @returns The portal domain
 */
export function getPortalDomain(): string {
    return DOMAINS.portaal;
}

/**
 * Get a domain value by its key
 * @param domain - The domain key
 * @returns The domain value (URL)
 *
 * @example
 * getDomainValue('elektra-app') // Returns the elektra-app domain URL from env
 */
export function getDomainValue(domain: Domain): string {
    return DOMAINS[domain];
}

/**
 * Build a redirect URL for login
 * @param from - The domain from which the redirect is initiated
 * @returns The redirect URL for login
 *
 * @example
 * getLoginRedirectUrl('elektra-app') // 'https://portaal.cdab.nl/redirect?redirectHost=...&from=elektra-app'
 */
export function getLoginRedirectUrl(from: Domain): string {
    const portalDomain = getPortalDomain();
    const protocol = portalDomain.includes('localhost') ? 'http:' : 'https:';
    const url = new URL('/redirect', `${protocol}//${portalDomain}`);

    url.searchParams.set('redirectHost', window.location.host);
    url.searchParams.set('redirectPath', window.location.pathname + window.location.search);
    url.searchParams.set('from', from);

    return url.toString();
}

/**
 * Build a redirect URL for logout
 * @param from - The domain from which the logout is initiated
 * @returns The redirect URL for logout
 *
 * @example
 * getLogoutRedirectUrl('elektra-app') // 'https://portaal.cdab.nl/logout?from=elektra-app'
 */
export function getLogoutRedirectUrl(from: Domain): string {
    const portalDomain = getPortalDomain();
    const protocol = portalDomain.includes('localhost') ? 'http:' : 'https:';
    const url = new URL('/logout', `${protocol}//${portalDomain}`);

    url.searchParams.set('from', from);

    return url.toString();
}

/**
 * Get the portal base URL
 * @returns The portal base URL
 *
 * @example
 * getPortalUrl() // 'https://portaal.cdab.nl'
 */
export function getPortalUrl(): string {
    const portalDomain = getPortalDomain();
    return buildUrl(portalDomain);
}

/**
 * Navigate to the portal
 * Redirects the current window to the portal base URL
 *
 * @example
 * navigateToPortal() // Redirects to portal
 */
export function navigateToPortal(): void {
    window.location.href = getPortalUrl();
}

/**
 * Navigate to portal login with redirect back to current location
 * @param from - The domain from which the login is initiated
 *
 * @example
 * navigateToPortalLogin('elektra-app') // Redirects to portal login with callback
 */
export function navigateToPortalLogin(from: Domain): void {
    window.location.href = getLoginRedirectUrl(from);
}

/**
 * Generate a redirect URL with authentication token
 * This function validates the redirect host against allowed hosts and creates a URL with the auth token
 *
 * @param params - The redirect parameters
 * @param params.redirectHost - The host to redirect to (e.g., 'localhost:5173', 'elektra.connected.nl')
 * @param params.redirectPath - Optional path to redirect to after login
 * @returns The full redirect URL with token, or null if validation fails
 *
 * @example
 * generateRedirectUrl({ redirectHost: 'localhost:5173', redirectPath: '/dashboard' })
 * // Returns: 'http://localhost:5173/login?token=...&redirect=/dashboard'
 */
export function generateRedirectUrl({
    redirectHost,
    redirectPath,
}: {
    redirectHost: string;
    redirectPath?: string;
}): string | null {
    // Validate redirect host against allowed hosts (secure exact-match validation)
    if (!isAllowedHost(redirectHost)) {
        console.warn(`Redirect host "${redirectHost}" is not in the list of allowed hosts.`);
        return null;
    }

    const token = getAuthentication();

    if (!token) {
        console.warn('No authentication token available for redirect.');
        return null;
    }

    // Ensure protocol is included - use http:// for localhost, https:// for others
    let fullUrl = redirectHost;
    if (!redirectHost.startsWith('http://') && !redirectHost.startsWith('https://')) {
        const protocol = redirectHost.startsWith('localhost') ? 'http://' : 'https://';
        fullUrl = protocol + redirectHost;
    }

    const url = new URL(fullUrl);

    url.pathname = '/login';
    url.searchParams.set('token', JSON.stringify(token));

    if (redirectPath) {
        url.searchParams.set('redirect', redirectPath);
    }

    return url.toString();
}

/**
 * Build a URL to navigate from portal to another domain with authentication token
 * This function builds a redirect URL to the target domain's login route with the current auth token
 *
 * @param to - The target domain key (e.g., 'elektra-app')
 * @param redirectPath - Optional path to redirect to after login in the target domain
 * @returns The full URL with token query parameter
 *
 * @example
 * const url = getDomainUrl('elektra-app', '/projects') // Returns URL to elektra-app with token
 * window.location.href = url; // Navigate to the URL
 */
export function getDomainUrl(to: Domain, redirectPath?: string): string {
    const token = getAuthentication();

    const targetDomain = getDomainValue(to);
    const protocol = targetDomain.includes('localhost') ? 'http:' : 'https:';
    const url = new URL('/login', `${protocol}//${targetDomain}`);

    // Only add token if available
    if (token) {
        url.searchParams.set('token', JSON.stringify(token));
    }

    if (redirectPath) {
        url.searchParams.set('redirect', redirectPath);
    }

    return url.toString();
}
